import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  AlertBanner,
  AlertBannerProvider,
  Button,
  Loader,
} from '@saviynt/design-system';

import FormBuilder from '../../components/FormBuilder/components/Form/FormBuilder';
import IntegrationPageError from '../../components/IntegrationPageError/IntegrationPageError';
import testIds from '../../constants/testIds';
import useLocalization from '../../hooks/use-localize';
import type { ConnectionSettingsDetails } from '../ManageCMDBDiscoveredApps/data-layer/discoveryConfigByIdTypes';
import { useSaveDiscoveryConfigMutation } from '../ManageCMDBDiscoveredApps/data-store/saveDiscoveryConfigSlice';

import useConnectionTemplate from './data-layer/use-connection-template';
import useLocalizedFields from './data-layer/use-localized-fields';

import './ConnectionSettings.css';

interface ConnectionSettingsProps {
  details: ConnectionSettingsDetails;
  status: string;
  sortOrder: number;
  instanceName: string;
  instanceKey: number;
  onDirtyChange: (isDirty: boolean) => void;
  onSaveError: (message: string) => void;
  onSaveSuccess: (message: string) => void;
}

/** Recursively replace @TOKENS@ in an object using tokenMap */
function replaceTokensDeep(
  value: unknown,
  tokenMap: Record<string, string>
): unknown {
  if (typeof value === 'string') {
    return value.replace(/@[^@]+@/g, (t) => tokenMap[t] ?? '');
  }

  if (Array.isArray(value)) {
    return value.map((v) => replaceTokensDeep(v, tokenMap));
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, replaceTokensDeep(v, tokenMap)])
    );
  }

  return value;
}

export default React.memo(function ConnectionSettings({
  details,
  status,
  sortOrder,
  instanceName,
  instanceKey,
  onDirtyChange,
  onSaveError,
  onSaveSuccess,
}: ConnectionSettingsProps) {
  const formatMessage = useLocalization();

  // Key to remount FormBuilder when defaults change
  const [formKey, setFormKey] = useState(0);

  // Keep parent's onDirtyChange in a stable ref
  const onDirtyRef = useRef(onDirtyChange);

  useEffect(() => {
    onDirtyRef.current = onDirtyChange;
  }, [onDirtyChange]);

  // Local inline banner state
  const [localBanner, setLocalBanner] = useState<{
    kind: 'Success' | 'Critical';
    title: string;
    description: string;
    visible: boolean;
  }>({ kind: 'Success', title: '', description: '', visible: false });
  const closeLocalBanner = () =>
    setLocalBanner((b) => ({ ...b, visible: false }));

  // Form state, dirty tracking, errors
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [dirtyLocal, setDirtyLocal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const isInitialized = useRef(false);
  const isDisabled = status === 'Disabled';

  // Save mutation
  const [saveConfig, { isLoading: isSaving }] =
    useSaveDiscoveryConfigMutation();

  // Field-change handler
  const handleFieldChange = useCallback(
    ({
      attributeName,
      value,
      hasError,
    }: {
      attributeName: string;
      value: string;
      hasError: boolean;
      payloadAtrributeName: string;
    }) => {
      if (!isInitialized.current) {
        return;
      }

      setFieldErrors((errs) => ({ ...errs, [attributeName]: hasError }));
      setFormData((prev) => {
        if (prev[attributeName] === value) {
          return prev;
        }

        onDirtyRef.current(true);
        setDirtyLocal(true);

        return { ...prev, [attributeName]: value };
      });
    },
    []
  );

  // Load and localize template fields
  const {
    templateFields,
    isLoading: tplLoading,
    isError: tplError,
    fetchTemplate,
  } = useConnectionTemplate(
    details.configuration.categories,
    handleFieldChange
  );
  const localizedFields = useLocalizedFields(templateFields);
  const finalFields = localizedFields.map((f) => ({
    ...f,
    fieldDisabled: isDisabled || f.fieldDisabled || false,
  }));

  // Seed formData & bump formKey when details or templates change
  const prevDetailsRef = useRef<ConnectionSettingsDetails | null>(null);

  useEffect(() => {
    if (tplLoading || finalFields.length === 0) {
      return;
    }

    if (prevDetailsRef.current === details) {
      return;
    }

    const initial: Record<string, string> = {};

    finalFields.forEach((f) => {
      const key = f.payloadAtrributeName;
      const saved = (details as unknown as Record<string, unknown>)[key];

      initial[f.attributeName] =
        saved != null ? String(saved) : f.defaultValue ?? '';
    });

    setFormData(initial);
    setDirtyLocal(false);
    setFieldErrors({});
    onDirtyRef.current(false);
    isInitialized.current = true;

    // force re-mount of FormBuilder to pick up blanked password
    setFormKey((k) => k + 1);

    prevDetailsRef.current = details;
  }, [details, tplLoading, finalFields]);

  // Save handler
  const handleSave = useCallback(async () => {
    const payload: Record<string, string> = {
      discoveryApplicationName: instanceName,
      appTypeKey: String(details.configuration.appTypeKey),
      categoryName: details.configuration.categoryName,
      connectiontype: details.configuration.dbConnectionType,
    };

    finalFields.forEach((f) => {
      payload[f.payloadAtrributeName] = formData[f.attributeName] ?? '';
    });

    const connAttr = details.configuration.categories
      .flatMap((c) => c.subcategories)
      .flatMap((s) => s.groups)
      .flatMap((g) => g.attributes)
      .find((a) => a.attributeName === 'ConnectionJSON');

    if (connAttr) {
      try {
        const rawObj: unknown = JSON.parse(
          connAttr.attributeDefaultValue ?? connAttr.attributeValue ?? ''
        );

        payload.ConnectionJSON = JSON.stringify(
          replaceTokensDeep(rawObj, formData) as Record<string, unknown>
        );
      } catch (e) {
        Logger.error('Failed to parse/replace ConnectionJSON', e);
      }
    }

    try {
      // unwrap the real response object
      const response = await saveConfig({
        id: instanceKey,
        tabs: [{ sortOrder, name: 'Connection_Settings', details: payload }],
      }).unwrap();

      // show the server‐returned message
      setLocalBanner({
        kind: 'Success',
        title: formatMessage('INTEGRATIONS.BUTTON.SAVED', 'Saved'),
        description: response.message,
        visible: true,
      });
      onSaveSuccess(response.message);

      // re-fetch and re-seed
      await fetchTemplate();
      isInitialized.current = false;
    } catch (err: unknown) {
      const errorData = err as { data?: { message?: string } };
      const raw =
        errorData.data?.message ||
        formatMessage('INTEGRATIONS.ERROR.SAVE_FAILED');
      const m = /errorMessage=([^,\]}]+)/.exec(raw);
      const msg = m ? m[1].trim() : raw;

      setLocalBanner({
        kind: 'Critical',
        title: formatMessage('INTEGRATIONS.BUTTON.SAVE_ERROR_TITLE'),
        description: msg,
        visible: true,
      });
      onSaveError(msg);
    }
  }, [
    formData,
    finalFields,
    saveConfig,
    details,
    instanceKey,
    sortOrder,
    instanceName,
    onSaveError,
    onSaveSuccess,
    formatMessage,
    fetchTemplate,
  ]);

  // Handle template‐load error
  if (tplError) {
    return (
      <IntegrationPageError
        illustrationKind='Error'
        heading='INTEGRATIONS.DISCOVERY.ERROR.HEADING'
        subHeading='INTEGRATIONS.DISCOVERY.ERROR.SUBHEADING'
        reload={fetchTemplate}
      />
    );
  }

  // Compute Save enablement
  const allRequired = finalFields.every((f) =>
    f.required ? (formData[f.attributeName] ?? '').trim().length > 0 : true
  );
  const noErrors = Object.values(fieldErrors).every((e) => !e);
  const canSave = !isDisabled && dirtyLocal && allRequired && noErrors;

  return (
    <section
      className='connection-settings-container'
      data-testid={testIds.CONNECTION_SETTINGS_CONTAINER}>
      {/* Inline child banner */}
      <AlertBannerProvider>
        <AlertBanner
          colorTheme={localBanner.kind}
          title={localBanner.title}
          description={localBanner.description}
          isVisible={localBanner.visible}
          onCancel={closeLocalBanner}
          className='connection-settings-container-alertBanner'
          dataTestId={testIds.CONNECTION_SETTINGS_ALERT_BANNER}
        />
      </AlertBannerProvider>

      {tplLoading ? (
        <Loader
          color='brand'
          format='main'
          kind='loop'
          className='connection-settings-container-loader'
          dataTestId={testIds.CONNECTION_SETTINGS_LOADER}
        />
      ) : (
        <FormBuilder
          key={`form-${formKey}`}
          fields={finalFields}
          className='connection-settings-container-form'
        />
      )}

      <div
        className='connection-settings-container-footer'
        data-testid={testIds.CONNECTION_SETTINGS_FOOTER}>
        <Button
          type='button'
          onClick={handleSave}
          isDisabled={!canSave || isSaving}
          isLoading={isSaving}
          loaderText={formatMessage('INTEGRATIONS.BUTTON.SAVING')}
          dataTestId={testIds.CONNECTION_SETTINGS_SAVE_BUTTON}>
          {formatMessage('INTEGRATIONS.BUTTON.SAVE')}
        </Button>
      </div>
    </section>
  );
});
